import { FormEvent, useState } from "react";
import { Page } from "../../adaptive/primitives";
import { SALES_DEMO_VERTICALS, salesDemoScenarioForText, salesDemoVertical } from "./catalog";
import { RestaurantCustomerExperience } from "./RestaurantCustomerExperience";
import { RestaurantCustomerHandoffTray } from "./RestaurantCustomerHandoffTray";
import { RestaurantDemoWorkspace } from "./RestaurantDemoWorkspace";
import {
  parseRestaurantCustomerPath,
  SALES_PLAYGROUND_PUBLIC_PATH,
} from "./restaurant-customer-routing";
import type { SalesDemoAction, SalesDemoMessage, SalesDemoScenario, SalesDemoVertical } from "./types";

export { SALES_PLAYGROUND_PUBLIC_PATH } from "./restaurant-customer-routing";

const NAMED_BUSINESSES: Readonly<Record<string, string>> = Object.freeze({
  "casa-nativa": "Casa Nativa",
  "kiwi-gastrobar": "Kiwi Gastrobar",
  "demo-servicios": "Demo Servicios",
});

function slugify(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("es")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "demo";
}

function titleFromSlug(slug: string): string {
  return slug
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function businessNameFor(vertical: SalesDemoVertical, slug: string | null): string {
  if (!slug) return vertical.defaultBusinessName;
  return NAMED_BUSINESSES[slug] ?? titleFromSlug(slug);
}

export function salesPlaygroundPath(verticalId = "gastronomia", businessSlug?: string | null, setup = false): string {
  const path = businessSlug ? `${SALES_PLAYGROUND_PUBLIC_PATH}/${encodeURIComponent(businessSlug)}` : SALES_PLAYGROUND_PUBLIC_PATH;
  return `${path}?vertical=${encodeURIComponent(verticalId)}${setup ? "&setup=1" : ""}`;
}

export function restaurantDemoAdminPath(businessSlug?: string | null): string {
  const slug = businessSlug || "casa-nativa";
  return `${SALES_PLAYGROUND_PUBLIC_PATH}/${encodeURIComponent(slug)}?vertical=gastronomia&view=admin`;
}

function welcomeMessage(businessName: string): SalesDemoMessage {
  return {
    id: `welcome-${businessName}`,
    role: "assistant",
    text: `Hola 👋 Soy el asistente de ${businessName}. ¿En qué puedo ayudarte?`,
  };
}

function actionTechnicalLabel(action: SalesDemoAction): string {
  return action.capability.replaceAll(".", " → ");
}

function SalesPlaygroundClient({
  navigate,
  vertical,
  businessSlug,
  businessName,
}: {
  navigate: (path: string) => void;
  vertical: SalesDemoVertical;
  businessSlug: string | null;
  businessName: string;
}) {
  const params = new URLSearchParams(window.location.search);
  const setupMode = params.get("setup") === "1";
  const [messages, setMessages] = useState<readonly SalesDemoMessage[]>([welcomeMessage(businessName)]);
  const [actions, setActions] = useState<readonly SalesDemoAction[]>([]);
  const [activeScenarioId, setActiveScenarioId] = useState<string | null>(null);
  const [activeStepIndex, setActiveStepIndex] = useState(-1);
  const [input, setInput] = useState("");
  const [setupBusinessName, setSetupBusinessName] = useState(businessName);
  const [setupVerticalId, setSetupVerticalId] = useState(vertical.id);

  const activeScenario = activeScenarioId
    ? vertical.scenarios.find((scenario) => scenario.id === activeScenarioId) ?? null
    : null;
  const hasNextStep = Boolean(activeScenario && activeStepIndex + 1 < activeScenario.steps.length);

  function startScenario(scenario: SalesDemoScenario, customerText?: string) {
    const step = scenario.steps[0]!;
    const nonce = Date.now();
    setActiveScenarioId(scenario.id);
    setActiveStepIndex(0);
    setMessages([
      welcomeMessage(businessName),
      { id: `customer-${nonce}-0`, role: "customer", text: customerText ?? step.customerMessage },
      { id: `assistant-${nonce}-0`, role: "assistant", text: step.assistantMessage },
    ]);
    setActions(step.actions);
  }

  function advanceScenario(customerText?: string) {
    if (!activeScenario) return;
    const nextIndex = activeStepIndex + 1;
    const step = activeScenario.steps[nextIndex];
    if (!step) return;
    const nonce = Date.now();
    setActiveStepIndex(nextIndex);
    setMessages((current) => [
      ...current,
      { id: `customer-${nonce}-${nextIndex}`, role: "customer", text: customerText ?? step.customerMessage },
      { id: `assistant-${nonce}-${nextIndex}`, role: "assistant", text: step.assistantMessage },
    ]);
    setActions((current) => [...current, ...step.actions]);
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const text = input.trim();
    if (!text) return;
    if (activeScenario && hasNextStep) advanceScenario(text);
    else startScenario(salesDemoScenarioForText(vertical, text), text);
    setInput("");
  }

  function reset() {
    setMessages([welcomeMessage(businessName)]);
    setActions([]);
    setActiveScenarioId(null);
    setActiveStepIndex(-1);
    setInput("");
  }

  function prepareDemo(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    navigate(salesPlaygroundPath(setupVerticalId, slugify(setupBusinessName), false));
  }

  return <Page className="sales-playground-page">
    <header className="sales-demo-client-header">
      <div>
        <p className="sales-demo-kicker">Demo preparada para {businessName}</p>
        <h1>{businessName}</h1>
        <p className="sales-demo-client-subtitle">Así podría atender ClickDialog a tus clientes y trabajar detrás de cada conversación.</p>
      </div>
      <div className="sales-demo-header-actions">
        <span className="sales-demo-mode-pill">Demostración</span>
        <button type="button" onClick={reset}>Reiniciar chat</button>
        <button type="button" onClick={() => navigate(salesPlaygroundPath(vertical.id, businessSlug, !setupMode))}>{setupMode ? "Cerrar configuración" : "Preparar otra demo"}</button>
      </div>
    </header>

    {setupMode && <form className="sales-demo-setup" onSubmit={prepareDemo}>
      <div><span className="sales-demo-kicker">Preparar demo</span><strong>Personalizala en menos de un minuto</strong><p>El nombre queda reflejado en la URL para poder abrir una demo distinta para cada prospecto.</p></div>
      <label>Nombre del negocio<input value={setupBusinessName} onChange={(event) => setSetupBusinessName(event.target.value)} required /></label>
      <label>Tipo de negocio<select value={setupVerticalId} onChange={(event) => setSetupVerticalId(event.target.value)}>{SALES_DEMO_VERTICALS.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
      <button className="primary-button" type="submit">Abrir demo personalizada</button>
    </form>}

    <div className="sales-demo-context-row"><div><span className="sales-demo-kicker">Tipo de negocio</span><strong>{vertical.name}</strong></div><p>{vertical.description}</p><small>Los datos y acciones de esta pantalla son simulados para demostración comercial.</small></div>
    <div className="sales-demo-layout">
      <aside className="sales-demo-scenarios" aria-label="Escenarios de venta"><div className="sales-demo-section-heading"><span className="sales-demo-kicker">Qué mostrar</span><h2>Situaciones reales</h2><p>Elegí la que más se parezca a lo que vive este negocio.</p></div><div className="sales-demo-scenario-list">{vertical.scenarios.map((scenario) => <button type="button" key={scenario.id} className={activeScenarioId === scenario.id ? "sales-demo-scenario active" : "sales-demo-scenario"} onClick={() => startScenario(scenario)}><strong>{scenario.name}</strong><span>{scenario.summary}</span><small>{scenario.steps.length} pasos</small></button>)}</div><div className="sales-demo-capabilities"><span className="sales-demo-kicker">Puede conectarse con</span><ul>{vertical.capabilities.map((capability) => <li key={capability}>{capability}</li>)}</ul></div></aside>
      <section className="sales-demo-conversation" aria-labelledby="sales-demo-conversation-title"><div className="sales-demo-phone-header"><div className="sales-demo-avatar" aria-hidden="true">{businessName.slice(0, 2).toUpperCase()}</div><div><strong id="sales-demo-conversation-title">{businessName}</strong><span>Asistente comercial</span></div><span className="sales-demo-online">Activo</span></div><div className="sales-demo-messages" aria-live="polite">{messages.map((message) => <div key={message.id} className={`sales-demo-message ${message.role}`}><span>{message.text}</span></div>)}</div>{activeScenario && <div className="sales-demo-progress"><div><span className="sales-demo-kicker">Escenario</span><strong>{activeScenario.name}</strong><small>Paso {activeStepIndex + 1} de {activeScenario.steps.length}</small></div>{hasNextStep && <button className="primary-button" type="button" onClick={() => advanceScenario()}>Siguiente paso</button>}{!hasNextStep && activeStepIndex >= 0 && <span className="sales-demo-complete">Escenario completo</span>}</div>}<form className="sales-demo-composer" onSubmit={submit}><input aria-label="Mensaje de prueba" placeholder={hasNextStep ? "Respondé como el cliente o usá Siguiente paso…" : "Escribí como si fueras un cliente…"} value={input} onChange={(event) => setInput(event.target.value)} /><button className="primary-button" type="submit" disabled={!input.trim()}>Enviar</button></form></section>
      <aside className="sales-demo-activity" aria-label="Actividad operativa"><div className="sales-demo-section-heading"><span className="sales-demo-kicker">Detrás de la conversación</span><h2>Qué ocurrió</h2><p>La conversación es sólo la interfaz. Acá se ve el trabajo que el sistema podría hacer por el negocio.</p></div>{actions.length === 0 ? <div className="sales-demo-empty-activity">Elegí una situación o escribí un mensaje para empezar.</div> : <ol className="sales-demo-action-list">{actions.map((action, index) => <li key={`${action.capability}-${index}`}><div className="sales-demo-action-index">{index + 1}</div><div><strong>{action.label}</strong><p>{action.detail}</p><span className="sales-demo-capability-name">{actionTechnicalLabel(action)}</span></div><span className="sales-demo-status">Sandbox</span></li>)}</ol>}</aside>
    </div>
  </Page>;
}

export function SalesPlayground({ navigate }: { navigate: (path: string) => void }) {
  const params = new URLSearchParams(window.location.search);
  const requestedVertical = params.get("vertical");
  const vertical = salesDemoVertical(requestedVertical);
  const route = parseRestaurantCustomerPath(window.location.pathname);
  const businessSlug = route.businessSlug;
  const businessName = businessNameFor(vertical, businessSlug);
  const demoSlug = businessSlug ?? slugify(businessName);

  if (vertical.id === "gastronomia" && params.get("view") === "admin") return <><RestaurantDemoWorkspace businessSlug={demoSlug} businessName={businessName} navigate={navigate} /><RestaurantCustomerHandoffTray businessSlug={demoSlug} businessName={businessName} /></>;
  if (vertical.id === "gastronomia") return <RestaurantCustomerExperience businessSlug={demoSlug} businessName={businessName} surface={route.surface} navigate={navigate} />;

  return <SalesPlaygroundClient navigate={navigate} vertical={vertical} businessSlug={businessSlug} businessName={businessName} />;
}
